import z from 'zod';

import { ImageCodec, PlateImage, UnityImage } from './Image';
import {
    CURRENT_VERSION,
    ElementId,
    emptyChildren,
    EmptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, UNITY_ELEMENT_TYPES } from '../constants';

const UnityRatingPollTextItem = z.object({
    version: Version,
    type: z.literal(ELEMENT_TYPES.TEXT_ITEM),
    templateElement: z.boolean().optional(),
    text: z.string(),
});

const UnityRatingPollText = z.object({
    version: Version,
    type: z.literal(UNITY_ELEMENT_TYPES.TEXT),
    templateElement: z.boolean().optional(),
    items: z.array(UnityRatingPollTextItem),
});

export const UnityRatingPoll = z.object({
    type: z.literal(ELEMENT_TYPES.RATING_POLL),
    elementId: UnityElementId,
    id: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    name: z.string(),
    text: UnityRatingPollText,
    image: UnityImage.nullish(),
});
export type UnityRatingPoll = z.infer<typeof UnityRatingPoll>;

export const PlateRatingPoll = z.object({
    type: z.literal(ELEMENT_TYPES.RATING_POLL),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        pollId: z.string().optional(),
        name: z.string(),
        text: z.string(),
        image: PlateImage.nullable(),
    }),
    children: EmptyChildren,
});
export type RatingPollElement = z.infer<typeof PlateRatingPoll>;

export const RatingPollCodec = z.codec(UnityRatingPoll, PlateRatingPoll, {
    decode: (value) => ({
        type: ELEMENT_TYPES.RATING_POLL,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.id ? { pollId: value.id } : {}),
            ...(value.templateElement ? { templateElement: value.templateElement } : {}),
            name: value.name,
            text: value.text?.items?.[0]?.text ?? '',
            image: value.image ? ImageCodec.decode(value.image) : null,
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        version: CURRENT_VERSION,
        type: ELEMENT_TYPES.RATING_POLL,
        elementId: value.id,
        ...(value.data.pollId ? { id: value.data.pollId } : {}),
        ...(value.data.templateElement ? { templateElement: value.data.templateElement } : {}),
        name: value.data.name,
        text: {
            version: CURRENT_VERSION,
            type: UNITY_ELEMENT_TYPES.TEXT,
            templateElement: false,
            items: value.data.text
                ? [
                      {
                          version: CURRENT_VERSION,
                          type: ELEMENT_TYPES.TEXT_ITEM,
                          templateElement: false,
                          text: value.data.text,
                      },
                  ]
                : [],
        },
        image: value.data.image ? ImageCodec.encode(value.data.image) : undefined,
    }),
});
